/**
 * What a report is written from (#169).
 *
 * The bar asked for three: a night when it ends, a week on Sunday, a month
 * on the first. None of them is a new measurement — every figure is already
 * in the till's nights and the recipe book — so what lives here is the
 * arithmetic that turns those into a period, its comparisons, and the one
 * thing neither screen reports today: what the bar actually sold, by
 * flavour.
 *
 * Deliberately free of Bedrock, of storage and of any clock but the one it
 * is handed. The model writes the prose; this decides what is true.
 */
import { toFlavourFamily, FLAVOUR_FAMILY_KEYS } from "./flavour.js";
/**
 * What the bar sold, by flavour family.
 *
 * Volume-weighted, not line-counted: a drink is what it is mostly made of,
 * and counting a dash of bitters equally with 45 mL of gin would say the
 * bar's customers love Spice. Dilution and anything with no family — water,
 * ice, saline — is left out rather than counted as a flavour nobody chose.
 *
 * The question it answers is "what are people drinking", so it is driven by
 * units sold, not by what is on the list: a menu of ten sours where only
 * the one Negroni sells is a bar whose customers want Bitter.
 */
export function flavourMix(unitsBySpec, specsById, ingredientsById) {
    const ml = new Map();
    for (const [specId, units] of Object.entries(unitsBySpec)) {
        if (units <= 0)
            continue;
        const spec = specsById.get(specId);
        if (!spec)
            continue;
        for (const line of spec.lines) {
            const family = toFlavourFamily(ingredientsById.get(line.ingredientId)?.aromaFamily ?? null);
            if (!family)
                continue;
            ml.set(family, (ml.get(family) ?? 0) + line.ml * units);
        }
    }
    const total = [...ml.values()].reduce((sum, n) => sum + n, 0);
    if (total === 0)
        return [];
    return [...ml.entries()]
        .map(([family, poured]) => ({ family, ml: Math.round(poured), share: poured / total }))
        .sort((a, b) => b.ml - a.ml || a.family.localeCompare(b.family));
}
/** Every family, so a report can say which went to nothing. */
export const ALL_FLAVOUR_FAMILIES = FLAVOUR_FAMILY_KEYS;
/**
 * Measure one window.
 *
 * `nights` counts the nights the till reported rather than the days in the
 * window, so a report can say "four nights" for a week the bar was shut on
 * three of — and so a comparison between a full week and a short one is not
 * quietly read as a collapse in trade.
 */
export function summarisePeriod(days, window, specsById, ingredientsById) {
    const within = days.filter((d) => d.serviceDate >= window.from && d.serviceDate <= window.to);
    const unitsBySpec = {};
    let takingsCents = 0;
    for (const day of within) {
        takingsCents += day.takingsCents;
        for (const [specId, n] of Object.entries(day.units)) {
            unitsBySpec[specId] = (unitsBySpec[specId] ?? 0) + n;
        }
    }
    const bySpec = Object.entries(unitsBySpec)
        .map(([specId, units]) => ({ specId, name: specsById.get(specId)?.name ?? specId, units }))
        .sort((a, b) => b.units - a.units || a.name.localeCompare(b.name));
    return {
        window,
        nights: within.length,
        takingsCents,
        units: bySpec.reduce((sum, s) => sum + s.units, 0),
        bySpec,
        flavour: flavourMix(unitsBySpec, specsById, ingredientsById),
    };
}
/** A finished period as a record, ready to store. */
export function reportRecord(brief, text, model, writtenAt) {
    return {
        kind: brief.kind,
        from: brief.current.window.from,
        to: brief.current.window.to,
        text,
        nights: brief.current.nights,
        takingsCents: brief.current.takingsCents,
        units: brief.current.units,
        model,
        writtenAt: writtenAt.toISOString(),
    };
}
/**
 * The report that covers exactly this window, if one has been written.
 *
 * Exactly, not overlapping: a screen showing last week wants last week's
 * read. The monthly report for the month that week fell in is a different
 * period and would be the wrong answer, not a near one.
 */
export function reportOfWindow(reports, window) {
    return reports.find((r) => r.from === window.from && r.to === window.to) ?? null;
}
/**
 * Which kind of report reads a period, once it has closed.
 *
 * A hand-picked range has none: nothing is written for a span somebody typed
 * in, and the nights inside it each have their own. Null says so, rather
 * than offering the month the range happens to sit in.
 */
export function reportKindOf(period) {
    switch (period.kind) {
        case "this-week":
        case "last-week":
            return "weekly";
        case "this-month":
        case "last-month":
            return "monthly";
        case "range":
            return null;
    }
}
/** Takings a night, so periods of different lengths can be compared at all. */
export function perNightCents(figures) {
    return figures.nights === 0 ? 0 : Math.round(figures.takingsCents / figures.nights);
}
/**
 * The move between two periods, per night, as a fraction. Null when there
 * is nothing to measure from: a bar that took nothing last month has not
 * grown infinitely, it has no comparison.
 */
export function change(now, before) {
    const was = perNightCents(before);
    return was === 0 ? null : (perNightCents(now) - was) / was;
}
/**
 * Families whose share moved by more than `threshold`, biggest first — what
 * a trend in flavour actually looks like when you have two periods.
 */
export function flavourShift(now, before, threshold = 0.02) {
    const was = new Map(before.map((f) => [f.family, f.share]));
    const is = new Map(now.map((f) => [f.family, f.share]));
    return [...new Set([...was.keys(), ...is.keys()])]
        .map((family) => ({ family, from: was.get(family) ?? 0, to: is.get(family) ?? 0 }))
        .filter((f) => Math.abs(f.to - f.from) >= threshold)
        .sort((a, b) => Math.abs(b.to - b.from) - Math.abs(a.to - a.from));
}
//# sourceMappingURL=report.js.map